import { useEffect, useRef, useState, useCallback } from 'react';
import { ContentProcessingStatus } from '../../../enums/ContentProcessingLogs';
import {
  ContentDocumentSchema,
  ContentProcessingLogSchema,
} from '../../../definitions/schemas';
import ContentInterface from '../../../graphql/Content/ContentInterface';
import Poll from '../../../Services/Poll';
import { contentDocumentProcessingFields } from '../../../graphql/Content/ContentFields';
import { isDocumentComplete, skipPolling } from '../helper';

// Keep track of documents that have completed conversion
const completedDocuments = new Map<string, ContentDocumentSchema>();

interface UseDocumentPollingProps {
  contentId: string;
  contentProcessingLog?: ContentProcessingLogSchema;
  initialDocument: ContentDocumentSchema;
  userContactId?: string;
}

interface UseDocumentPollingResult {
  document: ContentDocumentSchema;
  isLoading: boolean;
}

export const useDocumentPolling = (
  props: UseDocumentPollingProps
): UseDocumentPollingResult => {
  const { contentId, initialDocument, contentProcessingLog, userContactId } =
    props;

  // Check if document was previously completed
  const wasCompletedBefore = useRef(!!completedDocuments.get(contentId));
  const [document, setDocument] = useState(
    completedDocuments.get(contentId) || initialDocument
  );
  const [isLoading, setIsLoading] = useState(!wasCompletedBefore);
  const [hasFailed, setHasFailed] = useState(false);

  const pollRef = useRef<Poll>();

  const fetchDocument = useCallback(async () => {
    const contentInterface = new ContentInterface();
    const updatedContent = await contentInterface.get(
      {
        id: contentId,
      },
      contentDocumentProcessingFields
    );

    if (updatedContent?.document) {
      const status = updatedContent.processingLog?.status;
      if (status === ContentProcessingStatus.Failed) {
        setHasFailed(true);
        return { isLoading: false };
      }
      const isLoading =
        !isDocumentComplete(updatedContent.document) ||
        status === ContentProcessingStatus.Processing;

      if (!isLoading) {
        completedDocuments.set(contentId, updatedContent.document);
        wasCompletedBefore.current = true;
        setDocument(updatedContent.document);
        setIsLoading(false);
        pollRef.current?.stop();
      }

      return { isLoading };
    }

    return { isLoading: true };
  }, [contentId]);

  const pollDocument = useCallback(async () => {
    const { isLoading } = await fetchDocument();

    if (isLoading) {
      setIsLoading(true);
      return;
    }

    pollRef.current?.stop();
  }, [fetchDocument]);

  // Initialize polling
  useEffect(() => {
    // Return early if document is a PDF or already complete and not processing
    if (skipPolling(initialDocument, contentProcessingLog) || !!userContactId) {
      return;
    }

    // Don't create poll if document was processed before already or timed out
    if (wasCompletedBefore.current || hasFailed) {
      if (isLoading) {
        setIsLoading(false);
      }
      return;
    }

    const poll = new Poll(pollDocument);
    pollRef.current = poll;

    setIsLoading(true);
    poll.resume();

    return () => {
      poll.stop();
    };
  }, [
    contentId,
    isLoading,
    pollDocument,
    hasFailed,
    initialDocument.mime,
    initialDocument,
    contentProcessingLog,
    userContactId,
  ]);

  // Return early if document is a PDF or already complete and not processing
  if (skipPolling(initialDocument, contentProcessingLog) || !!userContactId) {
    return {
      document: initialDocument,
      isLoading: false,
    };
  }

  return {
    document,
    isLoading,
  };
};
